export const DEFAULT_NOISE_CANCELLATION_CAPABILITY = {
  default: {
    telephony_enabled: true,
    processor: null,
  },
  telephony: {
    supported_providers: ['plivo'],
    informational_text: 'Provider-native noise cancellation is currently supported only for Plivo and can be combined with the audio processor. For other providers, this setting has no effect.',
  },
  processors: [
    {
      engine: 'ai_acoustics',
      label: 'AI Acoustics',
      default_model: 'quail-vf-2.2-s-16khz',
      default_sensitivity: 0.25,
      sensitivity_min: 0,
      sensitivity_max: 1,
      sensitivity_step: 0.01,
      models: [
        {
          value: 'quail-vf-2.2-s-16khz',
          label: 'Voice Focus 2.2 Small',
          supports_sensitivity: true,
        },
        {
          value: 'quail-s-16khz',
          label: 'Background Noise Small',
          supports_sensitivity: false,
        },
      ],
    },
  ],
};

export const getNoiseCancellationCapability = (allowedConfig = {}) => {
  const supplied = allowedConfig?.noise_cancellation;
  if (!supplied || typeof supplied !== 'object' || Array.isArray(supplied)) {
    return DEFAULT_NOISE_CANCELLATION_CAPABILITY;
  }

  return {
    default: {
      ...DEFAULT_NOISE_CANCELLATION_CAPABILITY.default,
      ...(supplied.default || {}),
    },
    telephony: {
      ...DEFAULT_NOISE_CANCELLATION_CAPABILITY.telephony,
      ...(supplied.telephony || {}),
    },
    processors: Array.isArray(supplied.processors)
      ? supplied.processors
      : DEFAULT_NOISE_CANCELLATION_CAPABILITY.processors,
  };
};

export const getAiCousticsCapability = (allowedConfig = {}) => (
  getNoiseCancellationCapability(allowedConfig).processors
    .find(processor => processor.engine === 'ai_acoustics')
);

const normalizeSensitivity = (value, capability) => {
  const minimum = Number.isFinite(Number(capability.sensitivity_min))
    ? Number(capability.sensitivity_min)
    : 0;
  const maximum = Number.isFinite(Number(capability.sensitivity_max))
    ? Number(capability.sensitivity_max)
    : 1;
  const parsed = typeof value === 'boolean' || value === '' || value == null
    ? Number.NaN
    : Number(value);
  if (Number.isFinite(parsed) && parsed >= minimum && parsed <= maximum) return parsed;

  const defaultSensitivity = Number(capability.default_sensitivity);
  if (
    Number.isFinite(defaultSensitivity)
    && defaultSensitivity >= minimum
    && defaultSensitivity <= maximum
  ) {
    return defaultSensitivity;
  }
  return minimum;
};

export const normalizeNoiseCancellation = (raw, allowedConfig = {}) => {
  const capability = getNoiseCancellationCapability(allowedConfig);
  const source = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  const defaultTelephonyEnabled = capability.default?.telephony_enabled === true;
  const telephonyEnabled = typeof source.telephony_enabled === 'boolean'
    ? source.telephony_enabled
    : defaultTelephonyEnabled;
  const processor = source.processor;

  if (!processor || typeof processor !== 'object' || Array.isArray(processor)) {
    return { telephony_enabled: telephonyEnabled, processor: null };
  }

  const processorCapability = capability.processors
    .find(item => item.engine === processor.engine);
  if (!processorCapability || processorCapability.engine !== 'ai_acoustics') {
    return { telephony_enabled: telephonyEnabled, processor: null };
  }

  const models = Array.isArray(processorCapability.models)
    ? processorCapability.models
    : [];
  const model = models.find(item => item.value === processor.model)
    || models.find(item => item.value === processorCapability.default_model)
    || models[0];

  if (!model) return { telephony_enabled: telephonyEnabled, processor: null };

  const normalizedProcessor = {
    engine: 'ai_acoustics',
    model: model.value,
  };
  if (model.supports_sensitivity === true) {
    normalizedProcessor.sensitivity = normalizeSensitivity(
      processor.sensitivity,
      processorCapability,
    );
  }

  return {
    telephony_enabled: telephonyEnabled,
    processor: normalizedProcessor,
  };
};

export const setTelephonyNoiseCancellation = (raw, enabled, allowedConfig = {}) => {
  const normalized = normalizeNoiseCancellation(raw, allowedConfig);
  return {
    telephony_enabled: enabled === true,
    processor: normalized.processor,
  };
};

export const setNoiseCancellationProcessor = (raw, engine, allowedConfig = {}) => {
  const normalized = normalizeNoiseCancellation(raw, allowedConfig);
  if (engine !== 'ai_acoustics') {
    return { telephony_enabled: normalized.telephony_enabled, processor: null };
  }
  return normalizeNoiseCancellation({
    telephony_enabled: normalized.telephony_enabled,
    processor: { engine },
  }, allowedConfig);
};

export const getNoiseCancellationProcessorOptions = (allowedConfig = {}) => ([
  { value: null, label: 'None' },
  ...getNoiseCancellationCapability(allowedConfig).processors.map(processor => ({
    value: processor.engine,
    label: processor.label,
  })),
]);

export const getAiCousticsModelFromLabel = (label, allowedConfig = {}) => {
  const capability = getAiCousticsCapability(allowedConfig);
  return capability?.models?.find(model => model.label === label)?.value
    || capability?.default_model;
};

export const getAiCousticsModelLabel = (modelId, allowedConfig = {}) => {
  const capability = getAiCousticsCapability(allowedConfig);
  return capability?.models?.find(model => model.value === modelId)?.label || modelId;
};

export const modelSupportsSensitivity = (modelId, allowedConfig = {}) => {
  const capability = getAiCousticsCapability(allowedConfig);
  return capability?.models?.find(model => model.value === modelId)
    ?.supports_sensitivity === true;
};
